import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import Topbar from "@/components/Topbar";
import { Lightning, ChatCircleDots, X } from "@phosphor-icons/react";
import { toast } from "sonner";

const KIND_LABEL = {
  oil_change_due: "Troca de óleo próxima",
  service_due: "Revisão próxima",
  no_reply: "Sem resposta",
  follow_up: "Follow-up",
};

const STATUS_CLS = {
  pending: "bg-amber-50 text-amber-700",
  notified: "bg-blue-50 text-blue-700",
  engaged: "bg-indigo-50 text-indigo-700",
  scheduled: "bg-emerald-50 text-emerald-700",
  closed: "bg-slate-100 text-slate-600",
  dismissed: "bg-slate-100 text-slate-500 line-through",
};

export default function Events() {
  const [rows, setRows] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [running, setRunning] = useState(false);
  const nav = useNavigate();

  const load = async () => {
    const [e, c] = await Promise.all([api.get("/events"), api.get("/customers")]);
    setRows(e.data);
    setCustomers(c.data);
  };
  useEffect(() => { load(); }, []);

  const runEngine = async () => {
    setRunning(true);
    try {
      const { data } = await api.post("/events/run");
      toast.success(`${data.created} eventos criados`);
      load();
    } catch { toast.error("Falha"); } finally { setRunning(false); }
  };

  const notify = async (id) => {
    try {
      const { data } = await api.post(`/events/${id}/notify`);
      toast.success("Mensagem enviada ao cliente.");
      nav(`/app/conversas/${data.conversation_id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha");
    }
  };

  const dismiss = async (id) => {
    await api.post(`/events/${id}/dismiss`);
    load();
  };

  const name = (id) => customers.find((c) => c.id === id)?.name || "—";

  return (
    <>
      <Topbar
        title="Eventos"
        subtitle="Event Engine — gatilhos automáticos criados pelas regras."
        actions={
          <button onClick={runEngine} disabled={running} data-testid="events-run-btn" className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-slate-900 text-white hover:bg-slate-800 disabled:opacity-60 transition-colors text-[12px] font-medium shadow-sm"><Lightning size={13} weight="fill" /> {running ? "Rodando…" : "Rodar engine"}</button>
        }
      />
      <div className="p-6 md:p-8">
        <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-left">
              <tr>
                <th className="px-4 py-3 text-xs font-medium text-slate-600 uppercase tracking-wider">Tipo</th>
                <th className="px-4 py-3 text-xs font-medium text-slate-600 uppercase tracking-wider">Cliente</th>
                <th className="px-4 py-3 text-xs font-medium text-slate-600 uppercase tracking-wider">Veículo</th>
                <th className="px-4 py-3 text-xs font-medium text-slate-600 uppercase tracking-wider">Vence em</th>
                <th className="px-4 py-3 text-xs font-medium text-slate-600 uppercase tracking-wider">Status</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && <tr><td colSpan="6" className="p-6 text-center text-slate-500">Nenhum evento. Rode o engine.</td></tr>}
              {rows.map((e) => (
                <tr key={e.id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`event-row-${e.id}`}>
                  <td className="px-4 py-3 font-medium text-slate-900">{KIND_LABEL[e.kind] || e.kind}</td>
                  <td className="px-4 py-3 text-slate-700">{name(e.customer_id)}</td>
                  <td className="px-4 py-3 text-slate-700 font-mono text-xs">{e.payload?.label || e.payload?.plate || "—"}</td>
                  <td className="px-4 py-3 text-slate-700 text-xs">{e.due_at ? new Date(e.due_at).toLocaleDateString("pt-BR") : "—"}</td>
                  <td className="px-4 py-3"><span className={`text-[11px] uppercase tracking-wider px-2 py-1 rounded ${STATUS_CLS[e.status] || "bg-slate-100 text-slate-600"}`}>{e.status}</span></td>
                  <td className="px-4 py-3 text-right space-x-2">
                    {(e.status === "pending") && (
                      <button onClick={() => notify(e.id)} data-testid={`event-notify-${e.id}`} className="inline-flex items-center gap-1 rounded-md bg-emerald-600 text-white px-3 py-1.5 text-xs font-medium hover:bg-emerald-700"><ChatCircleDots size={14} weight="fill" /> Notificar</button>
                    )}
                    {(e.status === "notified") && (
                      <button onClick={() => nav("/app/conversas")} className="inline-flex items-center gap-1 rounded-full bg-slate-900 text-white px-3 py-1 text-[11px] font-medium hover:bg-slate-800 transition-colors"><ChatCircleDots size={11} weight="fill" /> Ver conversa</button>
                    )}
                    {e.status !== "dismissed" && e.status !== "scheduled" && (
                      <button onClick={() => dismiss(e.id)} className="p-1.5 rounded hover:bg-slate-100 text-slate-500"><X size={14} /></button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
}
